/**
 * Every rule about what may be stored, as pure functions.
 *
 * Both halves of the application call these: the browser to keep a person from
 * submitting something the server will refuse, and the Worker because a
 * browser check is a courtesy rather than a guarantee. The server is the only
 * one of the two that decides.
 */
import { err, ok, type Result } from './result';
import {
  EMAIL_STAGES,
  MAX_EMAIL_LENGTH,
  MAX_LABEL_LENGTH,
  MAX_SCALE_POINTS,
  MAX_STATEMENT_LENGTH,
  MAX_URL_LENGTH,
  MIN_SCALE_POINTS,
  STATEMENT_COUNT,
  type EmailStage,
  type SurveyConfig,
} from './types';

/** Anything longer than this is rejected before it is even normalized. */
const ABSURD_LENGTH = 10_000;

/** The canonical lower-case form of a version 4 UUID, and nothing else. */
const PARTICIPANT_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/** Control characters, which have no place in a statement and can corrupt a CSV. */
// eslint-disable-next-line no-control-regex -- matching them is the whole point
const CONTROL_CHARACTERS = /[\u0000-\u001F\u007F]/gu;

/**
 * One local part, one `@`, one domain with a dot in it, and no whitespace.
 *
 * Deliberately loose. The address is handed to Sway, which validates it
 * properly and answers with its own sentence if it will not have it; what this
 * catches is the typo a participant can fix on the spot — a missing `@`, a
 * space, a domain with no dot — without refusing an address that is unusual
 * but real.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

/**
 * Reduce free text to a single tidy line.
 *
 * Newlines and tabs become spaces rather than being rejected, because pasting
 * a statement out of a document is the normal way one arrives, and the paste
 * carries whatever wrapping the document had.
 */
export function normalizeLine(input: string): string {
  return input.normalize('NFC').replace(CONTROL_CHARACTERS, ' ').replace(/\s+/g, ' ').trim();
}

/** The shape an organizer submits; `version` is the server's to assign. */
export type ConfigInput = Omit<SurveyConfig, 'version'>;

function asString(input: unknown, what: string): Result<string> {
  if (typeof input !== 'string') return err(`${what} must be text.`);
  if (input.length > ABSURD_LENGTH) return err(`${what} is far too long.`);
  return ok(input);
}

export function validateStatements(input: unknown): Result<string[]> {
  if (!Array.isArray(input)) return err('The statements must be a list.');
  if (input.length !== STATEMENT_COUNT) {
    return err(`There must be exactly ${STATEMENT_COUNT} statements.`);
  }
  const cleaned: string[] = [];
  for (let i = 0; i < input.length; i++) {
    const raw = asString(input[i], `Statement ${i + 1}`);
    if (!raw.ok) return raw;
    const value = normalizeLine(raw.value);
    if (value === '') return err(`Statement ${i + 1} is empty.`);
    if (value.length > MAX_STATEMENT_LENGTH) {
      return err(`Statement ${i + 1} is longer than ${MAX_STATEMENT_LENGTH} characters.`);
    }
    cleaned.push(value);
  }
  return ok(cleaned);
}

export function validateScaleLabels(input: unknown): Result<string[]> {
  if (!Array.isArray(input)) return err('The response labels must be a list.');
  if (input.length < MIN_SCALE_POINTS || input.length > MAX_SCALE_POINTS) {
    return err(
      `There must be between ${MIN_SCALE_POINTS} and ${MAX_SCALE_POINTS} response labels.`,
    );
  }
  const cleaned: string[] = [];
  for (let i = 0; i < input.length; i++) {
    const raw = asString(input[i], `Response label ${i + 1}`);
    if (!raw.ok) return raw;
    const value = normalizeLine(raw.value);
    if (value === '') return err(`Response label ${i + 1} is empty.`);
    if (value.length > MAX_LABEL_LENGTH) {
      return err(`Response label ${i + 1} is longer than ${MAX_LABEL_LENGTH} characters.`);
    }
    cleaned.push(value);
  }
  // Two identical labels would put two indistinguishable buttons in front of a
  // person and two indistinguishable columns in the export.
  const seen = new Set(cleaned.map((label) => label.toLocaleLowerCase()));
  if (seen.size !== cleaned.length) return err('Two response labels are the same.');
  return ok(cleaned);
}

export function validatePostItem(input: unknown): Result<string> {
  const raw = asString(input, 'The extra question');
  if (!raw.ok) return raw;
  const value = normalizeLine(raw.value);
  if (value.length > MAX_STATEMENT_LENGTH) {
    return err(`The extra question is longer than ${MAX_STATEMENT_LENGTH} characters.`);
  }
  return ok(value);
}

/**
 * The address participants are sent to once they finish.
 *
 * https only, and no embedded credentials. This value ends up in
 * `location.assign`, so anything the scheme check let through — `javascript:`
 * above all — would be script execution handed to whoever could reach the
 * organizer page.
 */
export function validateSwayUrl(input: unknown): Result<string> {
  const raw = asString(input, 'The Sway link');
  if (!raw.ok) return raw;
  const trimmed = raw.value.trim();
  if (trimmed === '') return ok('');
  if (trimmed.length > MAX_URL_LENGTH) {
    return err(`The Sway link is longer than ${MAX_URL_LENGTH} characters.`);
  }
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return err('The Sway link is not a valid web address.');
  }
  if (url.protocol !== 'https:') return err('The Sway link must start with https://.');
  if (url.username !== '' || url.password !== '') {
    return err('The Sway link must not contain a username or password.');
  }
  return ok(url.toString());
}

/**
 * An email address a participant typed, lower-cased and trimmed.
 *
 * Lower-cased because Sway looks accounts up by address and stores them that
 * way; a participant who registers as `Ada@` before the event and is handed
 * off as `ada@` afterwards must be one person, not two.
 */
export function validateEmail(input: unknown): Result<string> {
  const raw = asString(input, 'The email address');
  if (!raw.ok) return raw;
  const value = raw.value.normalize('NFC').replace(CONTROL_CHARACTERS, '').trim().toLowerCase();
  if (value === '') return err('Please enter your email address.');
  if (value.length > MAX_EMAIL_LENGTH) {
    return err(`The email address is longer than ${MAX_EMAIL_LENGTH} characters.`);
  }
  if (!EMAIL_PATTERN.test(value)) return err('That does not look like an email address.');
  return ok(value);
}

/**
 * When an email address is asked for. Absent means never, which is what every
 * deployment did before the setting existed; anything else that is not one of
 * the three words is refused rather than guessed at.
 */
export function validateEmailStage(input: unknown): Result<EmailStage> {
  if (input === null || input === undefined) return ok('none');
  if (typeof input !== 'string' || !EMAIL_STAGES.includes(input as EmailStage)) {
    return err('When to ask for an email address must be "none", "pre" or "post".');
  }
  return ok(input as EmailStage);
}

/**
 * The randomization switch, which must be a boolean and not merely truthy.
 *
 * An absent field is `false` — that is what every deployment did before the
 * setting existed, and what the migration leaves the column at. Anything else
 * that is not a boolean is refused rather than coerced: a client sending the
 * string `"false"` and getting randomization switched on would be a silent
 * change to the instrument.
 */
export function validateRandomizeOrder(input: unknown): Result<boolean> {
  if (input === null || input === undefined) return ok(false);
  if (typeof input !== 'boolean') return err('Randomizing the order must be true or false.');
  return ok(input);
}

export function validateConfigInput(input: unknown): Result<ConfigInput> {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return err('Expected a settings object.');
  }
  const body = input as Record<string, unknown>;

  const statements = validateStatements(body['statements']);
  if (!statements.ok) return statements;
  const scaleLabels = validateScaleLabels(body['scaleLabels']);
  if (!scaleLabels.ok) return scaleLabels;
  const postItem = validatePostItem(body['postItem'] ?? '');
  if (!postItem.ok) return postItem;
  const randomizeOrder = validateRandomizeOrder(body['randomizeOrder']);
  if (!randomizeOrder.ok) return randomizeOrder;
  const swayUrl = validateSwayUrl(body['swayUrl'] ?? '');
  if (!swayUrl.ok) return swayUrl;
  const emailStage = validateEmailStage(body['emailStage']);
  if (!emailStage.ok) return emailStage;

  return ok({
    statements: statements.value,
    scaleLabels: scaleLabels.value,
    postItem: postItem.value,
    randomizeOrder: randomizeOrder.value,
    swayUrl: swayUrl.value,
    emailStage: emailStage.value,
  });
}

/**
 * Whether two configurations differ in any field a collected answer depends on.
 *
 * `randomizeOrder` is in here even though it changes no stored number. What it
 * changes is the sample: turn it on halfway through and some participants
 * answered a fixed order and some a varied one, and nothing in the export
 * would say which was which. That is a difference between people, which is the
 * thing this survey measures, so it freezes with the wording.
 */
export function instrumentChanged(current: SurveyConfig, next: ConfigInput): boolean {
  return (
    current.statements.length !== next.statements.length ||
    current.statements.some((statement, i) => statement !== next.statements[i]) ||
    current.scaleLabels.length !== next.scaleLabels.length ||
    current.scaleLabels.some((label, i) => label !== next.scaleLabels[i]) ||
    current.randomizeOrder !== next.randomizeOrder
  );
}

/**
 * Refuse an edit that would make already-collected answers unreadable.
 *
 * A stored answer is an ordinal position on a scale. Reword the statement or
 * change the scale after somebody has answered and the number stops meaning
 * what it meant when it was recorded — silently, because the row still parses.
 * So the instrument freezes the moment the first answer lands. The Sway link
 * and the email setting are not part of the instrument and stay editable
 * throughout, which matters on the day: they are the settings an organizer is
 * likely to get wrong.
 */
export function checkFreeze(
  current: SurveyConfig,
  next: ConfigInput,
  locks: { statements: boolean; postItem: boolean },
): Result<null> {
  if (locks.statements && instrumentChanged(current, next)) {
    return err(
      'Somebody has already answered, so the statements, the response labels and the order they are shown in can no longer change.',
    );
  }
  if (locks.postItem && current.postItem !== next.postItem) {
    return err(
      'Somebody has already answered after the event, so the extra question can no longer change.',
    );
  }
  return ok(null);
}

export function isParticipantId(input: unknown): input is string {
  return typeof input === 'string' && PARTICIPANT_ID_PATTERN.test(input);
}

/**
 * A random, anonymous identifier generated on the participant's device.
 *
 * `crypto.randomUUID` needs a secure context and a browser newer than Safari
 * 15.4; the fallback builds the same shape out of `getRandomValues`, which has
 * been available everywhere for a decade. Both paths satisfy
 * {@link isParticipantId}, so the server's check does not have to know which
 * one ran.
 */
export function newParticipantId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  // `noUncheckedIndexedAccess` is on, and a 16-byte array does have these two
  // — but `??` states that in a way the compiler checks rather than a cast.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-');
}

export function validateAnswers(input: unknown, scalePoints: number): Result<number[]> {
  if (!Array.isArray(input)) return err('Expected a list of answers.');
  if (input.length !== STATEMENT_COUNT) {
    return err(`Expected ${STATEMENT_COUNT} answers, one for each statement.`);
  }
  const cleaned: number[] = [];
  for (let i = 0; i < input.length; i++) {
    const value: unknown = input[i];
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 1 || value > scalePoints) {
      return err(`Answer ${i + 1} is not one of the ${scalePoints} available responses.`);
    }
    cleaned.push(value);
  }
  return ok(cleaned);
}

/**
 * The post-only item, which is optional for the organizer to ask and required
 * for a participant to answer.
 *
 * When there is no item, an answer to it is not merely ignored but refused:
 * silently dropping it would let a client store something the export has no
 * column for. When there is one, an absent answer is refused for the mirror
 * reason — a blank in that column would be missing for a reason nothing
 * records. See `docs/decisions.md` § "The extra question is optional to ask
 * and required to answer".
 */
export function validatePostItemAnswer(
  input: unknown,
  scalePoints: number,
  itemAsked: boolean,
): Result<number | null> {
  if (!itemAsked) {
    if (input === null || input === undefined) return ok(null);
    return err('There is no extra question to answer.');
  }
  if (input === null || input === undefined) return err('The extra question must be answered.');
  if (typeof input !== 'number' || !Number.isInteger(input) || input < 1 || input > scalePoints) {
    return err(`The extra answer is not one of the ${scalePoints} available responses.`);
  }
  return ok(input);
}
